import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Button,
  Skeleton,
  Alert,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  TableChart,
  Map as MapIcon,
  Route as RouteIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PdfIcon,
  GridOn as ExcelIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../store/store';
import { fetchLocationHistory, resetHistory } from '../../../store/slices/geoLocationSlice';
import { toolsApi, EmployeeOption } from '../../../api/toolsApi';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchOSRMRoute, RouteResult } from '../../../utils/routingUtils';
import type { LocationLogDto } from '../../../types';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Fix default marker icons for webpack/CRA
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const createNumberedIcon = (index: number, isCheckIn: boolean) =>
  L.divIcon({
    className: '',
    html: `<div style="background:${isCheckIn ? '#2e7d32' : '#ed6c02'};color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:bold;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">${index}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });

const getEmployeeLabel = (employee: EmployeeOption) => employee.name;

const LocationHistoryPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { history, loading, error } = useSelector((state: RootState) => state.geoLocation);
  const user = useSelector((state: RootState) => state.auth.user);
  const hasOwnGeoLocationOnlyAccess =
    !!user?.permissions?.includes('ViewOwnGeoLocation') && !user?.permissions?.includes('ViewGeoLocation');

  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [employeeId, setEmployeeId] = useState('');
  const [employeeError, setEmployeeError] = useState('');
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [viewMode, setViewMode] = useState<'table' | 'map' | 'route'>('table');
  const [routeData, setRouteData] = useState<RouteResult | null>(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [srNoSortDirection, setSrNoSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  const isInvalidDateRange = Boolean(
    startDate &&
    endDate &&
    new Date(startDate) > new Date(endDate)
  );

   useEffect(() => {
    toolsApi.getEmployees().then((result) => {
      setEmployees(result || []);
    }).catch(() => { });
  }, []);

  useEffect(() => {
    if (!hasOwnGeoLocationOnlyAccess || !user?.userId || employees.length === 0) {
      return;
    }

    const currentEmployee = employees.find((employee) => employee.id === user.userId);
    if (currentEmployee) {
      setEmployeeId(currentEmployee.id);
      setEmployeeError('');
    }
  }, [employees, hasOwnGeoLocationOnlyAccess, user?.userId]);

  const location = useLocation();

  const handleFetch = () => {
    if (!employeeId) {
      setEmployeeError('Employee field is required.');
      return;
    }
    setEmployeeError('');
    if (startDate && endDate) {
      dispatch(fetchLocationHistory({ employeeId, startDate, endDate }));
    }
  };

  useEffect(() => {
    if (employeeId) {
      handleFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  useEffect(() => {
    dispatch(resetHistory());
    if (employeeId) {
      handleFetch();
    }
    // Refresh history when the route is entered or the logged-in user changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user?.userId]);

  const validLocations = history.filter((l) => l.latitude !== 0 && l.longitude !== 0);
  const sortedHistoryRows = useMemo(() => {
    const rows = history.map((log, idx) => ({
      log,
      srNo: idx + 1,
    }));

    if (!srNoSortDirection) {
      return rows;
    }

    return rows.sort((a, b) =>
      srNoSortDirection === 'asc' ? a.srNo - b.srNo : b.srNo - a.srNo
    );
  }, [history, srNoSortDirection]);
  const orderedForMap = [...validLocations].reverse();
  const mapCenter: [number, number] = orderedForMap.length > 0
    ? [orderedForMap[0].latitude, orderedForMap[0].longitude]
    : [20.5937, 78.9629];
  const polylinePositions: [number, number][] = orderedForMap.map((l) => [l.latitude, l.longitude]);

  // Fetch OSRM route when switching to route view
  useEffect(() => {
    if (viewMode !== 'route' || orderedForMap.length < 2) {
      setRouteData(null);
      return;
    }
    let cancelled = false;
    const loadRoute = async () => {
      setRouteLoading(true);
      // OSRM has a limit on waypoints; batch if too many
      const waypoints: [number, number][] = orderedForMap.map((l) => [l.latitude, l.longitude]);
      const result = await fetchOSRMRoute(waypoints);
      if (!cancelled) {
        setRouteData(result);
        setRouteLoading(false);
      }
    };
    loadRoute();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, history]);

  const getExportRows = (data: Array<{ log: LocationLogDto; srNo: number }>) =>
    data.map(({ log, srNo }) => ({
      'Sr. No.': srNo,
      Employee: log.employeeName,
      Location: log.locationName || `${log.latitude}, ${log.longitude}`,
      Source: log.source || 'Web',
      'Captured At': new Date(log.capturedAt).toLocaleString(),
      Coordinates: `${log.latitude}, ${log.longitude}`,
    }));

  const handleExportExcel = () => {
    const rows = getExportRows(sortedHistoryRows);
    const ws = XLSX.utils.json_to_sheet(rows);
    const colWidths = Object.keys(rows[0] || {}).map((key) => ({
      wch: Math.max(key.length, ...rows.map((row) => String((row as any)[key]).length)) + 2,
    }));
    ws['!cols'] = colWidths;

    Object.keys(rows[0] || {}).forEach((_, colIdx) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIdx });
      if (ws[cellRef]) {
        ws[cellRef].s = {
          fill: { fgColor: { rgb: '673AB7' } },
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          alignment: { horizontal: 'center', vertical: 'center' },
          border: {
            bottom: { style: 'thin', color: { rgb: '000000' } },
          },
        };
      }
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Location History');
    const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbOut], { type: 'application/octet-stream' }), `Location_History_${startDate}_${endDate}.xlsx`);
    setExportAnchor(null);
  };

  const handleExportPdf = () => {
    const rows = getExportRows(sortedHistoryRows);
    const columns = Object.keys(rows[0] || {});
    const doc = new jsPDF({ orientation: 'landscape' });

    doc.setFontSize(16);
    doc.text('Location History', 14, 15);
    doc.setFontSize(10);
    doc.text(`Period: ${startDate} to ${endDate}`, 14, 22);
    doc.text(`Total Records: ${history.length}`, 14, 28);

    autoTable(doc, {
      startY: 33,
      head: [columns],
      body: rows.map((row) => columns.map((column) => (row as any)[column])),
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [103, 58, 183], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    doc.save(`Location_History_${startDate}_${endDate}.pdf`);
    setExportAnchor(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Location History
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
          >
            <ToggleButton value="table"><TableChart sx={{ mr: 0.5 }} /> Table</ToggleButton>
            <ToggleButton value="map"><MapIcon sx={{ mr: 0.5 }} /> Map</ToggleButton>
            <ToggleButton value="route"><RouteIcon sx={{ mr: 0.5 }} /> Route</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
            disabled={loading || history.length === 0}
            sx={{ textTransform: 'none' }}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
          >
            <MenuItem onClick={handleExportExcel}>
              <ListItemIcon><ExcelIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText>Export Excel</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleExportPdf}>
              <ListItemIcon><PdfIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Export PDF</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 280 }}
            options={employees}
            value={employees.find((emp) => emp.id === employeeId) || null}
            onChange={(_, newValue) => {
              setEmployeeId(newValue?.id || '');
              setEmployeeError(newValue?.id ? '' : 'Employee field is required.');
            }}
            getOptionLabel={getEmployeeLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  width: '100%',
                }}
              >
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                  {option.name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ minWidth: 72, textAlign: 'right', fontFamily: 'monospace' }}
                >
                  {option.employeeCode || '-'}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Employee"
                placeholder="Select employee"
                required
                error={Boolean(employeeError)}
                helperText={employeeError || undefined}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
          {/* <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          /> */}

           <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            size="small"
            error={isInvalidDateRange}
            helperText={
              isInvalidDateRange
                ? 'Start Date cannot be greater than End Date'
                : ''
            }
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />

          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            size="small"
            error={isInvalidDateRange}
            helperText={
              isInvalidDateRange
                ? 'End Date must be after Start Date'
                : ''
            }
            slotProps={{
              inputLabel: {
                shrink: true,
              },
              htmlInput: {
                min: startDate,
              },
            }}
          />

          <Button
            variant="contained"
            onClick={handleFetch}
            disabled={loading}
            sx={{
              minHeight: 40,
              alignSelf: 'flex-start',
              textTransform: 'none',
            }}
          >
            Search
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {viewMode === 'table' ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={srNoSortDirection || false}>
                  <TableSortLabel
                    active={Boolean(srNoSortDirection)}
                    direction={srNoSortDirection || 'asc'}
                    onClick={() => setSrNoSortDirection((current) => current === 'asc' ? 'desc' : 'asc')}
                  >
                    <strong>Sr. No.</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell><strong>Employee</strong></TableCell>
                <TableCell><strong>Location</strong></TableCell>
                <TableCell><strong>Source</strong></TableCell>
                <TableCell><strong>Captured At</strong></TableCell>
                <TableCell><strong>Coordinates</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <TableCell key={j}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No location records found
                  </TableCell>
                </TableRow>
              ) : (
                sortedHistoryRows.map(({ log, srNo }) => (
                  <TableRow key={log.id}>
                    <TableCell>{srNo}</TableCell>
                    <TableCell>{log.employeeName}</TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      {log.locationName || `${log.latitude}, ${log.longitude}`}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.source || 'Web'}
                        size="small"
                        color={log.source?.includes('Check-In') ? 'success' : log.source?.includes('Check-Out') ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(log.capturedAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {log.latitude}, {log.longitude}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <>
          {/* Route summary chips */}
          {viewMode === 'route' && routeData && (
            <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 3 }}>
              <Chip label={`Total Distance: ${routeData.distanceKm} km`} color="primary" variant="outlined" />
              <Chip label={`Est. Travel Time: ${routeData.durationMinutes} min`} color="secondary" variant="outlined" />
              <Chip label={`Stops: ${orderedForMap.length}`} color="info" variant="outlined" />
            </Paper>
          )}
          {viewMode === 'route' && routeLoading && (
            <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading road route...</Typography>
            </Paper>
          )}
          <Paper sx={{ height: 500, overflow: 'hidden', borderRadius: 2 }}>
            {orderedForMap.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography color="text.secondary">No location data to display on map</Typography>
              </Box>
            ) : (
              <MapContainer center={mapCenter} zoom={14} style={{ height: '100%', width: '100%' }} key={`${mapCenter[0]}-${mapCenter[1]}-${viewMode}`}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {orderedForMap.map((log, idx) => (
                  <Marker
                    key={log.id}
                    position={[log.latitude, log.longitude]}
                    icon={createNumberedIcon(idx + 1, (log.source || '').includes('Check-In'))}
                  >
                    <Popup>
                      <strong>#{idx + 1} — {log.source || 'Web'}</strong><br />
                      {log.locationName && <>{log.locationName}<br /></>}
                      {new Date(log.capturedAt).toLocaleString()}<br />
                      <small>{log.latitude}, {log.longitude}</small>
                    </Popup>
                  </Marker>
                ))}
                {/* Show OSRM road route when in route mode, otherwise show straight-line */}
                {viewMode === 'route' && routeData ? (
                  <Polyline
                    positions={routeData.coordinates}
                    pathOptions={{ color: '#d32f2f', weight: 4, opacity: 0.85 }}
                  />
                ) : polylinePositions.length > 1 ? (
                  <Polyline
                    positions={polylinePositions}
                    pathOptions={{ color: '#1976d2', weight: 3, dashArray: '10, 6' }}
                  />
                ) : null}
              </MapContainer>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default LocationHistoryPage;
